import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  FileSpreadsheet,
  Gauge,
  Loader2,
  RefreshCw,
  Users,
} from "lucide-react";

function formatDecimalHours(value) {
  return Number(value || 0).toFixed(2);
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(Number(value || 0));
}

function formatHours(value) {
  const totalMinutes = Math.round(Number(value || 0) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return `${minutes}m`;
  if (!minutes) return `${hours}h`;

  return `${hours}h ${minutes}m`;
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function titleCase(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

const PAY_CATEGORIES = [
  ["ordinary", "Ordinary"],
  ["evening", "Evening"],
  ["night", "Night"],
  ["saturday", "Saturday"],
  ["sunday", "Sunday"],
  ["public_holiday", "Public Holiday"],
];

export default function PayrollCentre() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [filter, setFilter] = useState("ready");
  const [expandedId, setExpandedId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  const loadPayroll = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.get(
        "/payroll-centre/declarations",
        { params: { status: filter } }
      );

      setRows(response.data?.rows || []);
      setTotals(response.data?.totals || {});
    } catch (error) {
      console.error("Failed to load payroll declarations", error);
      toast.error(
        error?.response?.data?.detail ||
          "Failed to load Payroll Centre"
      );
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadPayroll();
  }, [loadPayroll]);

  const categorySummary = useMemo(
    () => totals.category_totals || {},
    [totals]
  );

  const openPreview = async (declarationId) => {
    setActionId(declarationId);

    try {
      const response = await api.get(
        `/payroll-centre/declarations/${declarationId}/preview`
      );

      setPreview(response.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Failed to preview Xero payroll lines"
      );
    } finally {
      setActionId(null);
    }
  };

  const markExported = async (declarationId) => {
    const confirmed = window.confirm(
      "Mark this approved payroll declaration as exported to Xero? " +
      "This locks the declaration in the exported state."
    );

    if (!confirmed) return;

    setActionId(declarationId);

    try {
      await api.post(
        `/payroll-centre/declarations/${declarationId}/mark-exported`
      );

      toast.success("Payroll declaration marked as exported");
      setPreview(null);
      await loadPayroll();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Failed to mark payroll declaration exported"
      );
    } finally {
      setActionId(null);
    }
  };

  return (
    <div
      className="p-4 md:p-6 space-y-6"
      data-testid="payroll-centre-page"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payroll Centre</h1>
          <p className="text-sm text-muted-foreground">
            Payroll is sourced from approved weekly timesheet declarations.
          </p>
        </div>

        <button
          type="button"
          onClick={loadPayroll}
          disabled={loading}
          className="inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${
              loading ? "animate-spin" : ""
            }`}
          />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          ["ready", "Ready for Payroll"],
          ["exported", "Exported"],
          ["all", "All"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setFilter(value);
              setExpandedId(null);
              setPreview(null);
            }}
            className={
              filter === value
                ? "rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                : "rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-9">
        <SummaryCard icon={FileSpreadsheet} label="Declarations" value={totals.declarations || 0} />
        <SummaryCard icon={Users} label="Staff" value={totals.staff || 0} />
        <SummaryCard icon={Calendar} label="Visits" value={totals.visits || 0} />
        <SummaryCard icon={Clock} label="Hours" value={`${formatDecimalHours(totals.total_hours)} hrs`} />
        <SummaryCard icon={Gauge} label="Kilometres" value={`${Number(totals.total_km || 0).toFixed(1)} km`} />
        <SummaryCard
          icon={CheckCircle2}
          label="Ready"
          value={totals.ready || 0}
        />
        <SummaryCard
          icon={FileSpreadsheet}
          label="Earnings"
          value={formatCurrency(totals.earnings_amount)}
        />
        <SummaryCard
          icon={Gauge}
          label="Mileage Allowance"
          value={formatCurrency(totals.mileage_amount)}
        />
        <SummaryCard
          icon={Download}
          label="Gross Pay"
          value={formatCurrency(totals.gross_pay)}
        />
      </div>

      <div className="rounded-xl border bg-card p-4">
        <h2 className="mb-4 font-semibold">Pay Category Summary</h2>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {PAY_CATEGORIES.map(([key, label]) => (
            <div key={key} className="rounded-lg bg-muted p-3">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="mt-1 font-semibold">
                {formatDecimalHours(categorySummary[key])}
              </p>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[260px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border bg-card py-16 text-center">
          <FileSpreadsheet className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h2 className="font-semibold">
            {filter === "ready"
              ? "No approved timesheets awaiting payroll"
              : filter === "exported"
                ? "No exported payroll declarations"
                : "No payroll declarations found"}
          </h2>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => {
            const isExpanded = expandedId === row.id;
            const isExported =
              row.status === "exported_to_xero" ||
              row.xero_export_status === "exported";

            return (
              <div
                key={row.id}
                className="rounded-xl border bg-card"
              >
                <div className="p-4 md:p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-semibold">
                          {row.staff_name}
                        </h2>

                        <span
                          className={
                            isExported
                              ? "rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700"
                              : "rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700"
                          }
                        >
                          {isExported
                            ? "Exported"
                            : row.export_readiness?.ready
                              ? "Ready for Xero"
                              : "Xero Setup Required"}
                        </span>
                      </div>

                      <p className="mt-1 text-sm text-muted-foreground">
                        Pay week {formatDate(row.week_start)} to{" "}
                        {formatDate(row.week_end)}
                      </p>

                      {isExported && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Exported {formatDateTime(row.xero_exported_at)}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openPreview(row.id)}
                        disabled={actionId === row.id}
                        className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                      >
                        {actionId === row.id ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="mr-2 h-4 w-4" />
                        )}
                        Xero Preview
                      </button>

                      {!isExported && (
                        <button
                          type="button"
                          onClick={() => markExported(row.id)}
                          disabled={actionId === row.id}
                          className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Mark Exported
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() =>
                          setExpandedId(isExpanded ? null : row.id)
                        }
                        className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted"
                      >
                        {isExpanded ? (
                          <ChevronUp className="mr-2 h-4 w-4" />
                        ) : (
                          <ChevronDown className="mr-2 h-4 w-4" />
                        )}
                        {isExpanded ? "Hide" : "Details"}
                      </button>
                    </div>
                  </div>

                  {!isExported &&
                    !row.export_readiness?.ready && (
                      <div className="mt-5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
                        <p className="font-semibold">
                          Xero export setup is incomplete
                        </p>

                        <div className="mt-2 space-y-1">
                          {(
                            row.export_readiness?.issues || []
                          ).map((issue) => (
                            <p
                              key={issue.code}
                              className="text-sm"
                            >
                              • {issue.message}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                  <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
                    <Metric label="Visits" value={row.entry_count || 0} />
                    <Metric label="Total Hours" value={`${formatDecimalHours(row.total_hours)} hrs`} />
                    <Metric label="Kilometres" value={`${Number(row.total_km || 0).toFixed(1)} km`} />
                    <Metric
                      label="Approved"
                      value={formatDateTime(row.approved_at)}
                    />
                    <Metric
                      label="Earnings"
                      value={formatCurrency(row.earnings_amount)}
                    />
                    <Metric
                      label="Mileage Allowance"
                      value={formatCurrency(row.mileage_amount)}
                    />
                    <Metric
                      label="Gross Pay"
                      value={formatCurrency(row.gross_pay)}
                    />
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
                    {PAY_CATEGORIES.map(([key, label]) => (
                      <Metric
                        key={key}
                        label={label}
                        value={formatDecimalHours(row.category_totals?.[key])}
                        compact
                      />
                    ))}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t p-4 md:p-5">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[900px] text-sm">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="pb-3 pr-4">Date</th>
                            <th className="pb-3 pr-4">Client</th>
                            <th className="pb-3 pr-4">Service</th>
                            <th className="pb-3 pr-4">Actual</th>
                            <th className="pb-3 pr-4">Hours</th>
                            <th className="pb-3 pr-4">Category</th>
                            <th className="pb-3">KM</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(row.entries || []).map((entry) => (
                            <tr
                              key={entry.clock_id || entry.visit_id}
                              className="border-b last:border-0"
                            >
                              <td className="py-3 pr-4">{formatDate(entry.visit_date)}</td>
                              <td className="py-3 pr-4">{entry.client_name || "Unknown"}</td>
                              <td className="py-3 pr-4">{entry.service_type || "—"}</td>
                              <td className="py-3 pr-4">
                                {entry.actual_start || "—"} – {entry.actual_end || "—"}
                              </td>
                              <td className="py-3 pr-4">{formatDecimalHours(entry.worked_hours)}</td>
                              <td className="py-3 pr-4">{titleCase(entry.pay_category)}</td>
                              <td className="py-3">{Number(entry.travel_km || 0).toFixed(1)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border bg-background p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">Xero Payroll Preview</h2>
                <p className="text-sm text-muted-foreground">
                  {preview.declaration?.staff_name} — week of{" "}
                  {formatDate(preview.declaration?.week_start)}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setPreview(null)}
                className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
              >
                Close
              </button>
            </div>

            <div className="mt-5 space-y-3">
              {(preview.lines || []).map((line, index) => (
                <div
                  key={`${line.pay_category}-${index}`}
                  className="rounded-lg border p-4"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium">
                        {titleCase(line.pay_category)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {line.earnings_rate}
                      </p>
                      <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
                        Xero Rate ID:{" "}
                        {line.earnings_rate_id ||
                          "Not linked"}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="font-semibold">
                        {line.type === "mileage"
                          ? `${Number(line.units || 0).toFixed(1)} units`
                          : `${formatDecimalHours(line.hours)} hrs`}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(line.rate)} rate
                      </p>
                      <p className="font-semibold">
                        {formatCurrency(line.amount)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!preview.lines?.length && (
              <p className="mt-5 text-sm text-muted-foreground">
                No payable Xero lines were generated.
              </p>
            )}

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Metric
                label="Earnings"
                value={formatCurrency(
                  preview.totals?.earnings_amount
                )}
              />
              <Metric
                label="Mileage Allowance"
                value={formatCurrency(
                  preview.totals?.mileage_amount
                )}
              />
              <Metric
                label="Gross Pay"
                value={formatCurrency(
                  preview.totals?.gross_pay
                )}
              />
            </div>

            {preview.declaration?.status === "approved" &&
              preview.declaration?.export_readiness?.ready && (
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => markExported(preview.declaration.id)}
                  disabled={actionId === preview.declaration.id}
                  className="inline-flex items-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Confirm Exported to Xero
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <Icon className="mb-2 h-5 w-5 text-primary" />
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-bold">{value}</p>
    </div>
  );
}

function Metric({ label, value, compact = false }) {
  return (
    <div
      className={
        compact
          ? "rounded-lg bg-muted p-2.5"
          : "rounded-lg bg-muted p-3"
      }
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-semibold">{value}</p>
    </div>
  );
}
